import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  Badge,
  Button,
  Drawer,
  EmptyState,
  InlineError,
  Input,
  Panel,
  Select,
  SaveHint,
  Skeleton,
  Textarea,
  useDebouncedAutoSave,
} from '../../components/ui';
import { PageHeader } from '../../layouts/Shells';
import { formatDate } from '../listView/TaskTable';
import {
  useCampaigns,
  useContentItems,
  useCreateCampaign,
  useCreateContentItem,
  useDeleteCampaign,
  useDeleteContentItem,
  useIsStudioAdmin,
  useUpdateCampaign,
  useUpdateContentItem,
} from './hooks';
import {
  BUCKET_SUGGESTIONS,
  CAMPAIGN_STATUS_LABELS,
  CAMPAIGN_STATUS_TONES,
  CONTENT_STATUS_LABELS,
  CONTENT_STATUS_ORDER,
  CONTENT_STATUS_TONES,
  calendarWeeks,
  contentByStatus,
  contentErrorMessage,
  localIso,
  mondayRun,
  reviewQueue,
} from './types';
import { canEditModule, useMyPermissions } from '../auth/useMyPermissions';
import type { Campaign, CampaignStatus, ContentItem, ContentStatus } from './types';

/**
 * The Nurture Studio (unification, Stage 2): the six views of the Apps
 * Script "Nurture Platform", rebuilt on the Hub. Approval is admin-only and
 * the database enforces it; the interface only mirrors that rule.
 */

type StudioTab = 'run' | 'board' | 'table' | 'calendar' | 'campaigns' | 'review';

const TAB_LABELS: Record<StudioTab, string> = {
  run: 'Monday Run',
  board: 'Board',
  table: 'Table',
  calendar: 'Calendar',
  campaigns: 'Campaigns',
  review: 'Review Queue',
};

function campaignName(campaigns: Campaign[], id: string | null): string {
  if (id === null) {
    return '';
  }
  return campaigns.find((campaign) => campaign.id === id)?.name ?? '';
}

/** One content card, used by Board, Monday Run and Review Queue. */
function ContentCard({
  item,
  campaigns,
  onOpen,
  draggable = false,
}: {
  item: ContentItem;
  campaigns: Campaign[];
  onOpen: (item: ContentItem) => void;
  draggable?: boolean;
}) {
  return (
    <li className="board-card">
      <button
        type="button"
        className="board-card-button"
        draggable={draggable}
        onDragStart={(event) => {
          event.dataTransfer.setData('text/plain', item.id);
          event.dataTransfer.effectAllowed = 'move';
        }}
        onClick={() => onOpen(item)}
      >
        <span className="board-card-title">{item.title}</span>
        <span className="board-card-meta">
          {item.bucket === '' ? null : <span>{item.bucket}</span>}
          {campaignName(campaigns, item.campaign_id) === '' ? null : (
            <span>{campaignName(campaigns, item.campaign_id)}</span>
          )}
          {item.scheduled_on === null ? null : <span>{formatDate(item.scheduled_on)}</span>}
        </span>
      </button>
    </li>
  );
}

function ContentDetailDrawer({
  item,
  campaigns,
  onClose,
}: {
  item: ContentItem;
  campaigns: Campaign[];
  onClose: () => void;
}) {
  const isAdmin = useIsStudioAdmin();
  const update = useUpdateContentItem();
  const remove = useDeleteContentItem();
  const clone = useCreateContentItem();
  const [error, setError] = useState('');
  const [title, setTitle] = useState(item.title);
  const [bucket, setBucket] = useState(item.bucket);
  const [campaignId, setCampaignId] = useState(item.campaign_id ?? '');
  const [scheduledOn, setScheduledOn] = useState(item.scheduled_on ?? '');
  const [subject, setSubject] = useState(item.subject);
  const [body, setBody] = useState(item.body);
  const [notes, setNotes] = useState(item.notes);
  const [reviewNotes, setReviewNotes] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  function report(cause: unknown) {
    setError(contentErrorMessage(cause instanceof Error ? cause.message : ''));
  }

  /**
   * The detail fields are edited together and the body runs long, so they
   * save as one debounced write instead of seven independent ones. The
   * panel no longer closes on save: it is an inspector, not a form.
   */
  const detailSave = useDebouncedAutoSave<string>(
    useCallback(
      (snapshot: string) => {
        const changes = JSON.parse(snapshot) as Record<string, string | null>;
        setError('');
        return new Promise((resolve, reject) => {
          update.mutate(
            { itemId: item.id, changes },
            {
              onSuccess: resolve,
              onError: (cause) => {
                report(cause);
                reject(cause);
              },
            },
          );
        });
      },
      [item.id, update],
    ),
  );

  const snapshot = JSON.stringify({
    title: title.trim(),
    bucket: bucket.trim(),
    campaign_id: campaignId === '' ? null : campaignId,
    scheduled_on: scheduledOn === '' ? null : scheduledOn,
    subject,
    body,
    notes,
  });
  const openedWith = useRef(snapshot);
  const saveRef = useRef(detailSave.save);
  saveRef.current = detailSave.save;

  useEffect(() => {
    if (snapshot === openedWith.current) {
      return;
    }
    saveRef.current(snapshot);
  }, [snapshot]);

  function setStatus(next: ContentStatus) {
    setError('');
    update.mutate({ itemId: item.id, changes: { status: next } }, { onError: report });
  }

  function requestChanges() {
    if (reviewNotes.trim() === '') {
      setError('Write the change request first.');
      return;
    }
    setError('');
    update.mutate(
      {
        itemId: item.id,
        changes: { status: 'drafting', review_notes: reviewNotes.trim() },
      },
      { onSuccess: onClose, onError: report },
    );
  }

  const statusOptions = CONTENT_STATUS_ORDER.filter(
    (status) => isAdmin || (status !== 'approved' && item.status !== 'approved'),
  ).map((status) => ({ value: status, label: CONTENT_STATUS_LABELS[status] }));

  return (
    <Drawer
      open
      title={item.title}
      onClose={onClose}
      wide
      menuItems={
        // Any TAP user may delete their own clutter; approved items are the
        // exception and the database enforces it, so this menu only mirrors
        // the rule rather than being the rule.
        item.status === 'approved'
          ? undefined
          : [
              {
                // Opens the visible confirmation below. The old flow armed on
                // the first click and deleted on a second click of a menu
                // that had already closed — which read as a dead button. Same
                // fix as the Social Studio drawer, same report.
                label: 'Delete…',
                danger: true,
                onSelect: () => setConfirmingDelete(true),
              },
            ]
      }
    >
      {error === '' ? null : <InlineError>{error}</InlineError>}

      {!confirmingDelete || item.status === 'approved' ? null : (
        <div className="drawer-delete-confirm" role="alert">
          <p>Delete “{item.title}”? This cannot be undone.</p>
          <div className="deal-add-row">
            <Button
              variant="danger"
              size="sm"
              disabled={remove.isPending}
              onClick={() => {
                setError('');
                remove.mutate(item.id, {
                  onSuccess: onClose,
                  onError: (cause: Error) => {
                    setConfirmingDelete(false);
                    report(cause);
                  },
                });
              }}
            >
              {remove.isPending ? 'Deleting…' : 'Confirm delete'}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setConfirmingDelete(false)}>
              Keep it
            </Button>
          </div>
        </div>
      )}
      {item.status === 'approved' ? (
        <p className="drawer-autosave-note">
          Approved content cannot be deleted. Move it out of Approved first.
        </p>
      ) : null}
      <div className="review-facts">
        <Badge tone={CONTENT_STATUS_TONES[item.status]}>{CONTENT_STATUS_LABELS[item.status]}</Badge>
        {item.approved_at === null ? null : (
          <span className="review-fact">Approved {formatDate(item.approved_at)}</span>
        )}
        {item.review_notes === '' ? null : (
          <span className="review-fact">Change request: {item.review_notes}</span>
        )}
      </div>

      <Select
        label="Status"
        value={item.status}
        options={statusOptions}
        onChange={(event) => setStatus(event.target.value as ContentStatus)}
      />
      {isAdmin && item.status === 'for_review' ? (
        <div className="review-composer">
          <Textarea
            label="Request changes (sends it back to Drafting with this note)"
            rows={2}
            value={reviewNotes}
            onChange={(event) => setReviewNotes(event.target.value)}
          />
          <span className="review-actions">
            <Button size="sm" onClick={() => setStatus('approved')} disabled={update.isPending}>
              Approve
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={requestChanges}
              disabled={update.isPending}
            >
              Request changes
            </Button>
          </span>
        </div>
      ) : null}

      <h4 className="review-subheading">Details</h4>
      <p className="drawer-autosave-note">
        Changes save on their own. <SaveHint state={detailSave.state} error={detailSave.error} />
      </p>
      <Input label="Title" value={title} onChange={(event) => setTitle(event.target.value)} />
      <Input
        label="Bucket"
        value={bucket}
        list="studio-buckets"
        onChange={(event) => setBucket(event.target.value)}
      />
      <datalist id="studio-buckets">
        {BUCKET_SUGGESTIONS.map((suggestion) => (
          <option key={suggestion} value={suggestion} />
        ))}
      </datalist>
      <Select
        label="Campaign"
        value={campaignId}
        options={[
          { value: '', label: 'None' },
          ...campaigns.map((campaign) => ({ value: campaign.id, label: campaign.name })),
        ]}
        onChange={(event) => setCampaignId(event.target.value)}
      />
      <Input
        label="Scheduled date"
        type="date"
        value={scheduledOn}
        onChange={(event) => setScheduledOn(event.target.value)}
      />
      <Input label="Subject" value={subject} onChange={(event) => setSubject(event.target.value)} />
      <Textarea
        label="Body"
        rows={8}
        value={body}
        onChange={(event) => setBody(event.target.value)}
      />
      <Textarea
        label="Notes"
        rows={2}
        value={notes}
        onChange={(event) => setNotes(event.target.value)}
      />

      <span className="review-actions">
        <Button
          variant="secondary"
          onClick={() => {
            setError('');
            clone.mutate(
              {
                title: `${item.title} (copy)`,
                campaignId: item.campaign_id,
                bucket: item.bucket,
                scheduledOn: null,
                subject: item.subject,
                body: item.body,
                notes: item.notes,
              },
              { onSuccess: onClose, onError: report },
            );
          }}
          disabled={clone.isPending}
        >
          Clone
        </Button>
      </span>
    </Drawer>
  );
}

function NewContentDrawer({
  open,
  campaigns,
  onClose,
}: {
  open: boolean;
  campaigns: Campaign[];
  onClose: () => void;
}) {
  const create = useCreateContentItem();
  const [error, setError] = useState('');
  const [title, setTitle] = useState('');
  const [bucket, setBucket] = useState('');
  const [campaignId, setCampaignId] = useState('');

  function submit() {
    if (title.trim() === '') {
      setError('Give the idea a title first.');
      return;
    }
    setError('');
    create.mutate(
      {
        title,
        campaignId: campaignId === '' ? null : campaignId,
        bucket,
        scheduledOn: null,
        subject: '',
        body: '',
        notes: '',
      },
      {
        onSuccess: () => {
          setTitle('');
          setBucket('');
          setCampaignId('');
          onClose();
        },
        onError: (cause) => setError(contentErrorMessage(cause.message)),
      },
    );
  }

  return (
    <Drawer
      open={open}
      title="New content idea"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={create.isPending}>
            {create.isPending ? 'Adding…' : 'Add idea'}
          </Button>
        </>
      }
    >
      {error === '' ? null : <InlineError>{error}</InlineError>}
      <Input label="Title" value={title} onChange={(event) => setTitle(event.target.value)} />
      <Input
        label="Bucket"
        value={bucket}
        list="studio-buckets-new"
        onChange={(event) => setBucket(event.target.value)}
      />
      <datalist id="studio-buckets-new">
        {BUCKET_SUGGESTIONS.map((suggestion) => (
          <option key={suggestion} value={suggestion} />
        ))}
      </datalist>
      <Select
        label="Campaign"
        value={campaignId}
        options={[
          { value: '', label: 'None' },
          ...campaigns.map((campaign) => ({ value: campaign.id, label: campaign.name })),
        ]}
        onChange={(event) => setCampaignId(event.target.value)}
      />
    </Drawer>
  );
}

function CampaignsView({ campaigns }: { campaigns: Campaign[] }) {
  const isAdmin = useIsStudioAdmin();
  const create = useCreateCampaign();
  const update = useUpdateCampaign();
  const remove = useDeleteCampaign();
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [type, setType] = useState('');

  function submit() {
    if (name.trim() === '') {
      setError('Name the campaign first.');
      return;
    }
    setError('');
    create.mutate(
      { name, type, audience: '', startsOn: null, endsOn: null, notes: '' },
      {
        onSuccess: () => {
          setName('');
          setType('');
          setAdding(false);
        },
        onError: (cause) => setError(contentErrorMessage(cause.message)),
      },
    );
  }

  return (
    <div>
      {error === '' ? null : <InlineError>{error}</InlineError>}
      {campaigns.length === 0 ? (
        <p className="task-panel-text">No campaigns yet.</p>
      ) : (
        <ul className="my-task-list">
          {campaigns.map((campaign) => (
            <li key={campaign.id} className="my-task-row">
              <div className="my-task-main">
                <span className="my-task-title">{campaign.name}</span>
                <span className="my-task-project">
                  {campaign.type === '' ? 'No type' : campaign.type}
                </span>
              </div>
              <span className="review-actions">
                <Select
                  label={`Status of ${campaign.name}`}
                  value={campaign.status}
                  options={(Object.keys(CAMPAIGN_STATUS_LABELS) as CampaignStatus[]).map(
                    (status) => ({ value: status, label: CAMPAIGN_STATUS_LABELS[status] }),
                  )}
                  onChange={(event) => {
                    setError('');
                    update.mutate(
                      {
                        campaignId: campaign.id,
                        changes: { status: event.target.value as CampaignStatus },
                      },
                      { onError: (cause) => setError(contentErrorMessage(cause.message)) },
                    );
                  }}
                />
                <Badge tone={CAMPAIGN_STATUS_TONES[campaign.status]}>
                  {CAMPAIGN_STATUS_LABELS[campaign.status]}
                </Badge>
                {isAdmin ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setError('');
                      remove.mutate(campaign.id, {
                        onError: (cause) => setError(contentErrorMessage(cause.message)),
                      });
                    }}
                  >
                    Delete
                  </Button>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
      {adding ? (
        <div className="review-composer">
          <Input
            label="Campaign name"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <Input
            label="Type"
            value={type}
            placeholder="Nurture, Thematic…"
            onChange={(event) => setType(event.target.value)}
          />
          <span className="review-actions">
            <Button size="sm" onClick={submit} disabled={create.isPending}>
              {create.isPending ? 'Adding…' : 'Add campaign'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setAdding(false)}>
              Cancel
            </Button>
          </span>
        </div>
      ) : (
        <Button variant="secondary" size="sm" onClick={() => setAdding(true)}>
          Add campaign
        </Button>
      )}
    </div>
  );
}

export function ContentStudioPage() {
  const [searchParams] = useSearchParams();
  // Read-only on Nurture Studio keeps the board and loses the drag handle.
  const nurturePermissions = useMyPermissions();
  const canEditNurture = canEditModule(nurturePermissions.data, 'nurture_studio');
  const campaignsQuery = useCampaigns();
  const itemsQuery = useContentItems();
  const update = useUpdateContentItem();
  const [tab, setTab] = useState<StudioTab>('run');
  const [error, setError] = useState('');
  const [openItemId, setOpenItemId] = useState<string | null>(() => searchParams.get('item'));
  const [creating, setCreating] = useState(false);
  const [calendarCursor, setCalendarCursor] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [dragOverStatus, setDragOverStatus] = useState<ContentStatus | null>(null);

  const closeDetail = useCallback(() => setOpenItemId(null), []);
  const closeCreate = useCallback(() => setCreating(false), []);

  const campaigns = campaignsQuery.data ?? [];
  const items = useMemo(() => itemsQuery.data ?? [], [itemsQuery.data]);
  const openItem = items.find((item) => item.id === openItemId) ?? null;

  function report(cause: unknown) {
    setError(contentErrorMessage(cause instanceof Error ? cause.message : ''));
  }

  function moveTo(itemId: string, status: ContentStatus) {
    setError('');
    update.mutate({ itemId, changes: { status } }, { onError: report });
  }

  function body() {
    if (itemsQuery.isPending || campaignsQuery.isPending) {
      return <Skeleton lines={4} label="Loading the studio" />;
    }
    if (itemsQuery.isError || campaignsQuery.isError) {
      return (
        <EmptyState title="The studio could not be loaded" description="Refresh to try again." />
      );
    }

    if (tab === 'run') {
      const groups = mondayRun(items, localIso(new Date()));
      return groups.length === 0 ? (
        <EmptyState title="A quiet week" description="Nothing scheduled and nothing waiting." />
      ) : (
        <div>
          <p className="project-category-empty">
            The week at a glance: approved content waiting for a slot, then each day's scheduled
            sends. Click an item to open it and set its scheduled date.
          </p>
          {groups.map((group) => (
            <section key={group.label} aria-label={group.label}>
              <h3 className="my-task-bucket">
                {group.label} ({group.items.length})
              </h3>
              {group.items.length === 0 ? (
                <p className="project-category-empty">Nothing scheduled.</p>
              ) : (
                <ul className="board-column-cards">
                  {group.items.map((item) => (
                    <ContentCard
                      key={item.id}
                      item={item}
                      campaigns={campaigns}
                      onOpen={(opened) => setOpenItemId(opened.id)}
                    />
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      );
    }

    if (tab === 'board') {
      const columns = contentByStatus(items);
      return (
        <div className="board-view">
          {CONTENT_STATUS_ORDER.map((status) => (
            <div
              key={status}
              className={
                dragOverStatus === status ? 'board-column board-column--drop' : 'board-column'
              }
              onDragOver={(event) => {
                event.preventDefault();
                setDragOverStatus(status);
              }}
              onDragLeave={() => setDragOverStatus(null)}
              onDrop={(event) => {
                event.preventDefault();
                setDragOverStatus(null);
                const itemId = event.dataTransfer.getData('text/plain');
                if (itemId !== '') {
                  moveTo(itemId, status);
                }
              }}
            >
              <div className="board-column-header">
                <Badge tone={CONTENT_STATUS_TONES[status]}>{CONTENT_STATUS_LABELS[status]}</Badge>
                <span className="board-column-count">{columns[status].length}</span>
              </div>
              {columns[status].length === 0 ? (
                <p className="board-column-empty">Nothing here.</p>
              ) : (
                <ul className="board-column-cards">
                  {columns[status].map((item) => (
                    <ContentCard
                      key={item.id}
                      item={item}
                      campaigns={campaigns}
                      draggable={canEditNurture}
                      onOpen={(opened) => setOpenItemId(opened.id)}
                    />
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      );
    }

    if (tab === 'table') {
      return items.length === 0 ? (
        <EmptyState title="No content yet" description="Add the first idea." />
      ) : (
        <div className="task-table-scroll">
          <table className="task-table">
            <thead>
              <tr>
                <th className="task-heading">Title</th>
                <th className="task-heading">Status</th>
                <th className="task-heading">Bucket</th>
                <th className="task-heading">Campaign</th>
                <th className="task-heading">Scheduled</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td className="task-cell">
                    <button
                      type="button"
                      className="portal-review-button"
                      onClick={() => setOpenItemId(item.id)}
                    >
                      {item.title}
                    </button>
                  </td>
                  <td className="task-cell">
                    <Badge tone={CONTENT_STATUS_TONES[item.status]}>
                      {CONTENT_STATUS_LABELS[item.status]}
                    </Badge>
                  </td>
                  <td className="task-cell">{item.bucket}</td>
                  <td className="task-cell">{campaignName(campaigns, item.campaign_id)}</td>
                  <td className="task-cell">
                    {item.scheduled_on === null ? '—' : formatDate(item.scheduled_on)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (tab === 'calendar') {
      const weeks = calendarWeeks(calendarCursor.year, calendarCursor.month, items);
      const monthLabel = new Date(calendarCursor.year, calendarCursor.month, 1).toLocaleString(
        undefined,
        { month: 'long', year: 'numeric' },
      );
      return (
        <div>
          <div className="review-actions studio-cal-nav">
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                setCalendarCursor((cursor) => {
                  const date = new Date(cursor.year, cursor.month - 1, 1);
                  return { year: date.getFullYear(), month: date.getMonth() };
                })
              }
            >
              Previous
            </Button>
            <strong>{monthLabel}</strong>
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                setCalendarCursor((cursor) => {
                  const date = new Date(cursor.year, cursor.month + 1, 1);
                  return { year: date.getFullYear(), month: date.getMonth() };
                })
              }
            >
              Next
            </Button>
          </div>
          <table className="studio-calendar">
            <thead>
              <tr>
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((name) => (
                  <th key={name}>{name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map((week) => (
                <tr key={week[0]?.iso}>
                  {week.map((day) => (
                    <td key={day.iso} className={day.inMonth ? '' : 'studio-cal-outside'}>
                      <span className="studio-cal-day">{day.dayOfMonth}</span>
                      {day.items.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          className="studio-cal-item"
                          onClick={() => setOpenItemId(item.id)}
                        >
                          {item.title}
                        </button>
                      ))}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (tab === 'campaigns') {
      return <CampaignsView campaigns={campaigns} />;
    }

    const queue = reviewQueue(items);
    return queue.length === 0 ? (
      <EmptyState title="Nothing waiting for review" description="The queue is clear." />
    ) : (
      <ul className="board-column-cards">
        {queue.map((item) => (
          <ContentCard
            key={item.id}
            item={item}
            campaigns={campaigns}
            onOpen={(opened) => setOpenItemId(opened.id)}
          />
        ))}
      </ul>
    );
  }

  return (
    <>
      <PageHeader title="Nurture Studio" topbar />
      <Panel actions={<Button onClick={() => setCreating(true)}>New idea</Button>}>
        <nav className="tracker-tab-nav">
          {(Object.keys(TAB_LABELS) as StudioTab[]).map((key) => (
            <button
              key={key}
              type="button"
              className={`tracker-tab-button${tab === key ? ' tracker-tab-button--active' : ''}`}
              onClick={() => setTab(key)}
            >
              {TAB_LABELS[key]}
            </button>
          ))}
        </nav>
        {error === '' ? null : <InlineError>{error}</InlineError>}
        {body()}
      </Panel>
      {openItem === null ? null : (
        <ContentDetailDrawer item={openItem} campaigns={campaigns} onClose={closeDetail} />
      )}
      <NewContentDrawer open={creating} campaigns={campaigns} onClose={closeCreate} />
    </>
  );
}
